import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { LoadingRows, ErrorState } from '../components/States'
import { useToast } from '../components/Toasts'
import { BackButton } from '../components/BackButton'
import { useAsync } from '../lib/useAsync'
import { money } from '../lib/money'
import { pctLabel } from '../lib/bands'
import type { Pct } from '../types'
import { spreadChoices } from '../lib/spreads'


/**
 * One role's range, and the survey job its midpoint came from.
 *
 * **The same drawing as a list row, with room for it.** One role, so the bar
 * is taller and the three figures are written out beside it rather than
 * squeezed above it.
 *
 * **The figures are written out beside the bar**, not only drawn. The
 * non-functional requirements have always said a chart needs a text
 * equivalent, with a pay band as the example, and this is the screen where
 * that stops being hypothetical: somebody will be asked to read this number
 * aloud in a meeting.
 *
 * **It offers no way to accept a waiting median.** It says one is waiting and
 * sends you to the Suggested changes tab. One place asking for a decision is
 * the whole reason the two tabs exist, and a second Accept here would undo it.
 */
export function SalaryRangeDetail() {
  const { jdId = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [nonce, setNonce] = useState(0)
  const [busy, setBusy] = useState(false)

  const state = useAsync(() => api.salaryRange(jdId), [jdId, nonce])

  const changePercentile = async (value: string) => {
    setBusy(true)
    try {
      await api.setPercentile(jdId, value as Pct)
      setNonce((n) => n + 1)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  const changeSpread = async (value: string) => {
    setBusy(true)
    try {
      if (value === 'inherit') await api.clearRoleSpread(jdId)
      else await api.setRoleSpread(jdId, Number(value))
      setNonce((n) => n + 1)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <div className="screen screen--narrow"><LoadingRows /></div>
  if (state.error) return <div className="screen screen--narrow"><ErrorState message={state.error} /></div>
  if (!state.data) return null

  const r = state.data
  const hasRange = r.midpoint !== null

  return (
    <div className="screen screen--narrow">
      <BackButton to="salary ranges" onClick={() => navigate('/salary-ranges')} />

      <header className="screen__head">
        <h1>{r.title}</h1>
        <p className="subline">
          {r.family} &middot; {r.level}{r.location ? ` · ${r.location}` : ''}
        </p>
      </header>

      {r.outOfDate && (
        <p className="panel panel--warn">
          This role has been edited since it was benchmarked. The range below is built on the
          older version of it. Benchmark it again to bring it up to date.
        </p>
      )}

      {/*
        Said, not offered. Accepting belongs on the Suggested changes tab,
        because one place asking for a decision is why there are two tabs.
      */}
      {r.waiting && (
        <p className="panel panel--note">
          The market has moved from <strong>{money(r.waiting.from, r.currency)}</strong> to{' '}
          <strong>{money(r.waiting.to, r.currency)}</strong>. This range stays as it is until
          somebody accepts it on{' '}
          <button type="button" className="linkish" onClick={() => navigate('/salary-ranges')}>
            Suggested changes
          </button>.
        </p>
      )}

      {hasRange
        ? (
          <>
            <section className="card" aria-labelledby="range-h">
              <h2 id="range-h" className="card__label">The range</h2>

              <div className="rangebar">
                <div className="rangebar__figures">
                  <div>
                    <p className="card__label">Minimum</p>
                    <p className="rangebar__edge num">{money(r.minimum, r.currency)}</p>
                  </div>
                  <div className="rangebar__centre">
                    <p className="card__label card__label--on">Midpoint</p>
                    <p className="rangebar__mid num">{money(r.midpoint, r.currency)}</p>
                  </div>
                  <div className="rangebar__right">
                    <p className="card__label">Maximum</p>
                    <p className="rangebar__edge num">{money(r.maximum, r.currency)}</p>
                  </div>
                </div>

                {/*
                  Drawn, and said. A band that exists only as a shape is a band
                  half the readers cannot use, so the three figures above are
                  the text equivalent of this and are not decoration.
                */}
                <div
                  className="rangebar__track"
                  role="img"
                  aria-label={`Range ${money(r.minimum, r.currency)} to ${money(r.maximum, r.currency)}, midpoint ${money(r.midpoint, r.currency)}`}
                >
                  <div className="rangebar__fill" style={{ width: `${40 + (r.spread / 25) * 60}%` }} />
                  <div className="rangebar__pin" />
                </div>

                <p className="subline">
                  {r.currency ?? 'Currency not recorded'}
                  {r.unit ? ` · ${r.unit}` : ''} · the <strong>{pctLabel(r.percentile)}</strong> percentile,
                  moved <strong>±{r.spread}%</strong> each way
                  {r.acceptedBy ? ` · accepted by ${r.acceptedBy}` : ''}
                </p>
              </div>
            </section>

            <section className="card" aria-labelledby="dials-h">
              <h2 id="dials-h" className="card__label">Where this one sits, and how wide it is</h2>

              {/*
                The percentiles this role's own survey published, and no
                others. A role with one gets no control: a choice of one is
                not a choice, and a dropdown that cannot be changed reads as
                a thing that is broken.
              */}
              <div className="actions">
                {r.percentiles.length < 2
                  ? (
                    <span className="dials__fixed">
                      The {pctLabel(r.percentile)} percentile
                    </span>
                  )
                  : (
                    <select
                      className="select"
                      value={r.percentile}
                      disabled={busy}
                      aria-label="Target percentile for this role"
                      onChange={(e) => void changePercentile(e.target.value)}
                    >
                      {r.percentiles.map((p) => (
                        <option key={p} value={p}>The {pctLabel(p)} percentile</option>
                      ))}
                    </select>
                  )}
                <span className="subline">
                  {r.percentiles.length < 2
                    ? 'The survey behind this role published no other percentile.'
                    : 'Changing this moves the range at once. Nobody approves it.'}
                </span>
              </div>

              <div className="actions">
                <select
                  className={`select${r.overridden ? ' select--own' : ''}`}
                  value={r.overridden ? String(r.spread) : 'inherit'}
                  disabled={busy}
                  aria-label="Range spread for this role"
                  onChange={(e) => void changeSpread(e.target.value)}
                >
                  {/*
                    Every option is a percentage, the way the table offers
                    them. The first is the company's own number, so choosing
                    it puts the role back on the shared one. It is offered
                    once: an explicit option for the same percentage would
                    read identically and do something subtly different.
                  */}
                  <option value="inherit">± {r.companySpread}%</option>
                  {spreadChoices(r.overridden ? r.spread : undefined).filter((p) => p !== r.companySpread).map((p) => (
                    <option key={p} value={p}>± {p}%</option>
                  ))}
                </select>
                <span className="subline">
                  {r.overridden
                    ? `This role has its own. Choose ± ${r.companySpread}% to put it back on the company's.`
                    : 'This role uses the company spread.'}
                </span>
              </div>
            </section>

            <section className="card" aria-labelledby="src-h">
              <h2 id="src-h" className="card__label">Where the midpoint came from</h2>
              {r.cuts.length === 0
                ? <p className="subline">The survey job behind this range is no longer held.</p>
                : (
                  <div className="table-wrap">
                    <table className="table--figures">
                      <thead>
                        <tr>
                          <th scope="col">Survey job</th>
                          <th scope="col">Source</th>
                          <th scope="col">Location</th>
                          {/* Headed with the percentile it shows, so a role
                              sitting at the 75th has no column called Median. */}
                          <th scope="col" className="num">{pctLabel(r.percentile)}</th>
                          <th scope="col" className="num">Organisations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.cuts.map((cut) => (
                          <tr key={cut.code}>
                            <td>
                              <strong>{cut.title}</strong>
                              <p className="subline">{cut.code}</p>
                            </td>
                            <td>{[cut.provider, cut.year].filter(Boolean).join(' ') || '—'}</td>
                            <td>{cut.location ?? '—'}</td>
                            <td className="num">{money(cut.median, r.currency)}</td>
                            <td className="num">{cut.orgs}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </section>
          </>
        )
        : (
          <section className="card">
            <h2 className="card__label">No range</h2>
            <p>
              {r.state === 'never-run'
                ? 'This role has not been benchmarked yet. Benchmark it and a range appears here.'
                : r.noMatchReason
                  || 'The survey does not cover this role, so there is no market median to build a range from.'}
            </p>
          </section>
        )}
    </div>
  )
}
